/* ACCESS REQUESTS › ROLE REQUESTS · PARTNER ACCOUNTS · TEAMS & ROLES (Jay tasks 02/11 #1160 / #1155). The decisions behind a
   sign-up: the role a person's team asks for (approve it, approve another, or reject with a reason), the partner accounts that
   wait before they can sign in, and the team → role table people pick from. The server (signupAccess.js) decides every rule;
   this draws it. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Check, X, Plus, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import './SignupCard.css';

const API = `${BACKEND_URL}/api/admin`;
// the server's times are UTC; people read Vietnam's (dd/Mon/yyyy hh:mm)
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const when = (v) => {
  const ms = Date.parse(v);
  if (!v || Number.isNaN(ms)) return '';
  const d = new Date(ms + 7 * 3600000).toISOString();
  return `${d.slice(8, 10)}/${MON[Number(d.slice(5, 7)) - 1]}/${d.slice(0, 4)} ${d.slice(11, 16)}`;
};
const errOf = (e) => e?.response?.data?.error || e.message;

export function RoleRequestsTab({ onChanged }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState(null);
  const [pick, setPick] = useState({});          // id → role chosen to give
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/role-requests`, { withCredentials: true }).then((r) => setD(r.data)).catch((e) => setMsg({ bad: true, text: errOf(e) })), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, decision, role) => {
    setBusy(true); setMsg(null);
    try {
      await axios.post(`${API}/role-requests/${id}/decide`, { decision, role, note: decision === 'rejected' ? note : undefined }, { withCredentials: true });
      setRejecting(null); setNote(''); await load(); if (onChanged) onChanged();
    } catch (e) { setMsg({ bad: true, text: errOf(e) }); } finally { setBusy(false); }
  };
  if (!d) return msg ? <p className="sp-error">{msg.text}</p> : <p className="sp-sub">…</p>;
  const waiting = d.requests.filter((r) => r.status === 'pending');
  const rest = d.requests.filter((r) => r.status !== 'pending');
  const person = (r) => <><b>{r.fullName || r.userName}</b><div className="sp-sub">{r.email}</div></>;
  const status = (s) => t(`xSignup.st_${s}`);
  return (
    <div data-su-roles>
      <p className="sp-note">{t('xSignup.rrIntro')}</p>
      {msg && <p className={msg.bad ? 'sp-error' : 'sp-ok'}>{msg.text}</p>}
      <section className="sp-section">
        <h3 className="sp-section-title">{t('xSignup.waiting', { n: waiting.length })}</h3>
        {!waiting.length ? <p className="sp-sub">{t('xSignup.none')}</p> : (
          <div className="sp-table-wrap"><table className="sp-table">
            <thead><tr><th>{t('xSignup.colPerson')}</th><th>{t('xSignup.colEntity')}</th><th>{t('xSignup.colTeam')}</th><th>{t('xSignup.colAsked')}</th><th>{t('xSignup.colWhen')}</th><th>{t('xSignup.colActions')}</th></tr></thead>
            <tbody>
              {waiting.map((r) => (
                <tr key={r.id} data-su-req={r.id}>
                  <td>{person(r)}</td><td>{r.entityKey || '—'}</td><td>{r.team || '—'}</td><td><b>{r.requestedRoleName || r.requestedRole}</b></td><td>{when(r.updatedAt)}</td>
                  <td>
                    {rejecting === r.id ? (
                      <div className="su-reject">
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('xSignup.reason')} data-su-note />
                        <button type="button" className="sp-btn danger" disabled={busy || !note.trim()} onClick={() => decide(r.id, 'rejected')} data-su-confirmreject><X size={13} /> {t('xSignup.confirmReject')}</button>
                        <button type="button" className="sp-btn" onClick={() => { setRejecting(null); setNote(''); }}>{t('xSignup.cancel')}</button>
                      </div>
                    ) : (
                      <div className="su-actions">
                        <select value={pick[r.id] || r.requestedRole} onChange={(e) => setPick({ ...pick, [r.id]: e.target.value })} aria-label={t('xSignup.roleToGive')} data-su-role={r.id}>
                          {d.roles.map((o) => <option key={o.roleKey} value={o.roleKey}>{o.roleName}</option>)}
                        </select>
                        <button type="button" className="sp-btn primary" disabled={busy} onClick={() => decide(r.id, 'approved', pick[r.id] || r.requestedRole)} data-su-approve={r.id}><Check size={13} /> {t('xSignup.approve')}</button>
                        <button type="button" className="sp-btn danger" disabled={busy} onClick={() => { setRejecting(r.id); setNote(''); }} data-su-reject={r.id}><X size={13} /> {t('xSignup.reject')}</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
      {rest.length > 0 && (
        <section className="sp-section">
          <h3 className="sp-section-title">{t('xSignup.decided')}</h3>
          <div className="sp-table-wrap"><table className="sp-table">
            <thead><tr><th>{t('xSignup.colPerson')}</th><th>{t('xSignup.colEntity')}</th><th>{t('xSignup.colTeam')}</th><th>{t('xSignup.colAsked')}</th><th>{t('xSignup.colStatus')}</th><th>{t('xSignup.colDecided')}</th></tr></thead>
            <tbody>
              {rest.map((r) => (
                <tr key={r.id}>
                  <td>{person(r)}</td><td>{r.entityKey || '—'}</td><td>{r.team || '—'}</td><td>{r.requestedRoleName || r.requestedRole || 'User'}</td>
                  <td>{status(r.status)}{r.status === 'approved' && r.decidedRoleName ? ` · ${r.decidedRoleName}` : ''}</td>
                  <td>{r.decidedBy ? `${r.decidedBy} · ${when(r.decidedAt)}` : '—'}{r.note ? <div className="sp-sub">{r.note}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </section>
      )}
    </div>
  );
}

export function PartnerAccountsTab({ onChanged }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/pending-accounts`, { withCredentials: true }).then((r) => setRows(r.data.accounts)).catch((e) => setMsg({ bad: true, text: errOf(e) })), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (id, decision) => {
    setBusy(true); setMsg(null);
    try { await axios.post(`${API}/pending-accounts/${id}/decide`, { decision }, { withCredentials: true }); await load(); if (onChanged) onChanged(); }
    catch (e) { setMsg({ bad: true, text: errOf(e) }); } finally { setBusy(false); }
  };
  return (
    <div data-su-partners>
      <p className="sp-note">{t('xSignup.paIntro')}</p>
      {msg && <p className={msg.bad ? 'sp-error' : 'sp-ok'}>{msg.text}</p>}
      {!rows ? <p className="sp-sub">…</p> : !rows.length ? <p className="sp-sub">{t('xSignup.none')}</p> : (
        <div className="sp-table-wrap"><table className="sp-table">
          <thead><tr><th>{t('xSignup.colPerson')}</th><th>{t('xSignup.colEmail')}</th><th>{t('xSignup.colOrg')}</th><th>{t('xSignup.colCreated')}</th><th>{t('xSignup.colActions')}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.userId}>
                <td><b>{r.fullName || r.userName}</b></td><td>{r.email}</td><td>{r.org || '—'}</td><td>{when(r.createdAt)}</td>
                <td className="su-actions">
                  <button type="button" className="sp-btn primary" disabled={busy} onClick={() => decide(r.userId, 'approved')}><Check size={13} /> {t('xSignup.approve')}</button>
                  <button type="button" className="sp-btn danger" disabled={busy} onClick={() => decide(r.userId, 'rejected')}><X size={13} /> {t('xSignup.reject')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

/* THE TEAM → ROLE TABLE (Max 02/11: "tab này thiết kế chưa okie", then "expand và collapse bảng của entity … default sẽ là
   collapse"): not a sheet of inputs but the portal's own list — one table, a folding group per company (all folded at first),
   Excel sort + filter on every header (they work inside each group), the header frozen, the role and the state as chips, and
   an Action column with a labelled Edit (never a "…" menu). Adding and changing a team is a small dialog.
   Since 03/11 (Jay task #1182) the rows ARE the portal's departments (00_PORTAL_sop_teams) — one list for sign-up, Edit user,
   Project Tracker and Catchy; a row's id is its department key. */
function TeamDialog({ team, d, onClose, onSaved, t }) {
  const isNew = !team.id;
  const [f, setF] = useState({ entityKey: team.entityKey || d.entities[0]?.key || '', name: team.name || '', role: team.role || '',
    key: '', sortOrder: team.sortOrder ?? 100, isActive: team.isActive !== false && team.isActive !== 0 });
  const locked = !!team.isSalesOp;   // a Sales OP team stays on (the server says the same)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const body = { ...f, role: f.role || null, key: isNew ? f.key : undefined, sortOrder: Number(f.sortOrder) || 0 };
    try {
      if (isNew) await axios.post(`${API}/signup-teams`, body, { withCredentials: true });
      else await axios.put(`${API}/signup-teams/${team.id}`, body, { withCredentials: true });
      onSaved();
    } catch (x) { setErr(errOf(x)); setBusy(false); }
  };
  return (
    <div className="su-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="su-dialog" role="dialog" aria-modal="true" aria-labelledby="su-dlg-h" onSubmit={save} data-su-teamdlg>
        <div className="su-dlg-head"><h3 id="su-dlg-h">{isNew ? t('xSignup.add') : t('xSignup.editTeam', { name: team.name })}</h3>
          <button type="button" className="su-x" onClick={onClose} aria-label={t('xSignup.cancel')}><X size={18} /></button></div>
        <div className="su-dlg-body">
          <label className="su-fld"><span>{t('xSignup.entity')}</span>
            <select value={f.entityKey} onChange={(e) => setF({ ...f, entityKey: e.target.value })} disabled={!isNew} data-su-f="entity">
              {d.entities.map((o) => <option key={o.key} value={o.key}>{o.key} · {o.name}</option>)}
            </select></label>
          <label className="su-fld"><span>{t('xSignup.team')} <b className="su-req">*</b></span>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required autoFocus data-su-f="name" /></label>
          <label className="su-fld"><span>{t('xSignup.colRole')}</span>
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-su-f="role">
              <option value="">{t('xSignup.userRole')}</option>
              {d.roles.map((o) => <option key={o.roleKey} value={o.roleKey}>{o.roleName}</option>)}
            </select>
            <small>{t('xSignup.roleHint')}</small></label>
          <label className="su-fld"><span>{t('xSignup.keyLabel')}</span>
            {isNew ? <input value={f.key} maxLength={12} onChange={(e) => setF({ ...f, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} data-su-f="key" />
              : <input value={team.id} disabled data-su-f="key" />}
            {isNew && <small>{t('xSignup.keyHint')}</small>}</label>
          <div className="su-fld-row">
            <label className="su-fld"><span>{t('xSignup.colOrder')}</span>
              <input type="number" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} data-su-f="order" /></label>
            <label className="su-fld su-check"><input type="checkbox" checked={f.isActive} disabled={locked} onChange={(e) => setF({ ...f, isActive: e.target.checked })} data-su-f="active" /> {t('xSignup.inUseLong')}</label>
          </div>
          {locked && <small className="su-note" data-su-locked>{t('xSignup.salesOpLocked')}</small>}
          {!isNew && !locked && !f.isActive && team.isActive && <small className="su-note su-warn" data-su-offhint>{t('xSignup.offHint', { n: team.members || 0 })}</small>}
          {err && <p className="sp-error">{err}</p>}
        </div>
        <div className="su-dlg-foot">
          <button type="button" className="sp-btn" onClick={onClose}>{t('xSignup.cancel')}</button>
          <button type="submit" className="sp-btn primary" disabled={busy || !f.name.trim()} data-su-dlgsave>{busy ? t('xSignup.saving') : t('xSignup.save')}</button>
        </div>
      </form>
    </div>
  );
}

export function TeamsRolesTab() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState(null);
  const [opened, setOpened] = useState(() => new Set());   // the companies unfolded — none at first (Max)
  const [editing, setEditing] = useState(null);     // a team, or {} for a new one
  const load = useCallback(() => axios.get(`${API}/signup-teams`, { withCredentials: true }).then((r) => setD(r.data)).catch((e) => setMsg({ bad: true, text: errOf(e) })), []);
  useEffect(() => { load(); }, [load]);
  const roleName = (k) => (k ? d?.roles.find((r) => r.roleKey === k)?.roleName || k : t('xSignup.userShort'));
  const rows = useMemo(() => (d ? d.teams : []), [d]);
  const columns = [
    { key: 'entityKey', get: (x) => x.entityKey },
    { key: 'name', get: (x) => x.name },
    { key: 'role', get: (x) => roleName(x.role) },
    { key: 'members', get: (x) => Number(x.members || 0) },
    { key: 'sortOrder', get: (x) => Number(x.sortOrder) },
    { key: 'isActive', get: (x) => (x.isActive ? t('xSignup.inUse') : t('xSignup.off')) },
  ];
  const tools = useColumnTools(rows, columns);
  if (!d) return msg ? <p className="sp-error">{msg.text}</p> : <p className="sp-sub">…</p>;
  // the groups in the companies' own order, each holding its rows as sorted and filtered
  const groups = d.entities.map((e) => ({ ...e, rows: tools.shown.filter((x) => x.entityKey === e.key), all: d.teams.filter((x) => x.entityKey === e.key).length }))
    .filter((g) => g.all);
  const allOpen = groups.length > 0 && groups.every((g) => opened.has(g.key));
  const toggle = (k) => setOpened((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <div data-su-teams>
      <p className="sp-note">{t('xSignup.trIntro')}</p>
      {msg && <p className={msg.bad ? 'sp-error' : 'sp-ok'}>{msg.text}</p>}
      <div className="su-bar">
        <button type="button" className="sp-btn" onClick={() => setOpened(allOpen ? new Set() : new Set(groups.map((g) => g.key)))} data-su-foldall>
          {allOpen ? <ChevronRight size={13} /> : <ChevronDown size={13} />} {allOpen ? t('xSignup.collapseAll') : t('xSignup.expandAll')}
        </button>
        {tools.active && <button type="button" className="sp-btn" onClick={tools.clear}>{t('xSignup.clearFilters')}</button>}
        <span className="su-shown">{t('xSignup.shownOf', { n: tools.shown.length, m: rows.length })}</span>
        <button type="button" className="sp-btn primary" onClick={() => setEditing({ entityKey: 'HEVN' })} data-su-addteam><Plus size={13} /> {t('xSignup.add')}</button>
      </div>
      <div className="su-scroll">
        <table className="sp-table su-table">
          <thead><tr>
            <SortTh label={t('xSignup.entity')} {...tools.thProps('entityKey')} />
            <SortTh label={t('xSignup.team')} {...tools.thProps('name')} />
            <SortTh label={t('xSignup.colRole')} {...tools.thProps('role')} />
            <SortTh label={t('xSignup.colMembers')} className="num" {...tools.thProps('members')} />
            <SortTh label={t('xSignup.colOrder')} className="num" {...tools.thProps('sortOrder')} />
            <SortTh label={t('xSignup.colStatus')} {...tools.thProps('isActive')} />
            <th className="su-act">{t('xSignup.colActions')}</th>
          </tr></thead>
          <tbody>
            {groups.map((g) => {
              const open = opened.has(g.key);
              return (
                <React.Fragment key={g.key}>
                  <tr className="su-group" data-su-group={g.key}>
                    <td colSpan={7}>
                      <button type="button" className="su-fold" aria-expanded={open} onClick={() => toggle(g.key)} data-su-fold={g.key}>
                        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        <span className="su-co">{g.key}</span> <b>{g.name}</b>
                        <span className="su-gn">{g.rows.length === g.all ? t('xSignup.teamsN', { n: g.all }) : t('xSignup.teamsOf', { n: g.rows.length, m: g.all })}</span>
                      </button>
                    </td>
                  </tr>
                  {open && g.rows.map((x) => (
                    <tr key={x.id} className={x.isActive ? '' : 'su-off'} data-su-team={x.id}>
                      <td><span className="su-co">{x.entityKey}</span></td>
                      <td><b>{x.name}</b> <span className="su-key">{x.id}</span></td>
                      <td>{x.role ? <span className="su-chip role">{roleName(x.role)}</span> : <span className="su-chip user">{t('xSignup.userShort')}</span>}</td>
                      <td className="num">{x.members || 0}</td>
                      <td className="num">{x.sortOrder}</td>
                      <td><span className={`su-chip ${x.isActive ? 'on' : 'off'}`}>{x.isActive ? t('xSignup.inUse') : t('xSignup.off')}</span></td>
                      <td className="su-act"><button type="button" className="sp-btn small" onClick={() => setEditing(x)} data-su-edit={x.id}><Pencil size={12} /> {t('xSignup.edit')}</button></td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!tools.shown.length && <tr><td colSpan={7} className="sp-sub">{t('xSignup.none')}</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <TeamDialog team={editing} d={d} t={t} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); setMsg({ text: t('xSignup.saved') }); }} />}
    </div>
  );
}
